<template>
  <div class="attendance-filters">
    <div class="filters-card">
      <div class="ts-grid ts-grid-cols-1 md:ts-grid-cols-2 lg:ts-grid-cols-4 ts-gap-4">
        <div class="filter-group">
          <label class="filter-label required">
            <Icon type="ios-calendar" size="14" />
            <span>{{ $t('attendanceGallery.dateRange') || 'Date Range' }}</span>
          </label>
          <DatePicker
            v-model="localFilters.dateRange"
            type="daterange"
            :placeholder="$t('selectDateRange') || 'Select date range'"
            format="dd-MM-yyyy"
            class="ts-w-full date-picker-custom"
            @on-change="handleDateRangeChange"
            size="large"
          />
        </div>

        <div class="filter-group">
          <label class="filter-label">
            <Icon type="ios-business" size="14" />
            <span>{{ $t('attendanceGallery.organisationalStructure') || 'Organization' }}</span>
          </label>
          <Select
            v-model="localFilters.org_chart_structure_id"
            clearable
            filterable
            placeholder="All Organizations"
            class="ts-w-full select-custom"
            @on-change="handleOrgChange"
            size="large"
          >
            <Option
              v-for="org in orgChartStructures"
              :key="org.org_chart_structure_id"
              :value="org.org_chart_structure_id"
            >
              {{ org.org_chart_structure }}
            </Option>
          </Select>
        </div>

        <div class="filter-group">
          <label class="filter-label">
            <Icon type="ios-people" size="14" />
            <span>{{ $t('attendanceGallery.employeeName') || 'Employee' }}</span>
          </label>
          <Select
            v-model="localFilters.employee_id"
            multiple
            filterable
            clearable
            :placeholder="$t('all') || 'All Employees'"
            :loading="employeeLoading"
            class="ts-w-full select-custom"
            size="large"
          >
            <Option
              v-for="employee in employees"
              :key="employee.employee_id"
              :value="employee.employee_id"
            >
              {{ employee.employee_name_en }}
            </Option>
          </Select>
        </div>

        <div class="filter-group">
          <label class="filter-label" style="visibility: hidden;">Action</label>
          <ts-button
            type="primary"
            class="ts-w-full search-button"
            :waiting="loading"
            @click="applyFilters"
            size="large"
          >
            <Icon type="ios-search" class="ts-mr-2" />
            {{ $t('view') || 'View Gallery' }}
          </ts-button>
        </div>
      </div>
      
      <div class="quick-filters" v-if="showQuickFilters">
        <div class="quick-filters-label">
          <Icon type="ios-funnel" size="14" />
          <span>Quick Filters:</span>
        </div>
        <div class="quick-filter-buttons">
          <ts-button size="small" @click="setToday">Today</ts-button>
          <ts-button size="small" @click="setYesterday">Yesterday</ts-button>
          <ts-button size="small" @click="setThisWeek">This Week</ts-button>
          <ts-button size="small" @click="setThisMonth">This Month</ts-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'AttendanceGalleryFilters',
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localFilters: {
        dateRange: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ],
        org_chart_structure_id: null,
        employee_id: []
      },
      employeeLoading: false,
      showQuickFilters: true
    }
  },
  computed: {
    ...mapState('report/attendanceGallery', [
      'orgChartStructures',
      'employees',
      'filters'
    ])
  },
  methods: {
    ...mapActions('report/attendanceGallery', [
      'getEmployee',
      'clearEmployee'
    ]),
    
    handleDateRangeChange(dates) {
      if (dates && dates.length === 2) {
        this.localFilters.dateRange = dates
      }
    },
    
    async handleOrgChange(orgId) {
      this.localFilters.employee_id = []
      if (orgId) {
        await this.loadEmployees(orgId)
      } else {
        await this.clearEmployee()
      }
    },
    
    async loadEmployees(orgId) {
      this.employeeLoading = true
      try {
        await this.getEmployee({ id: orgId })
      } catch (error) {
        console.error('Failed to load employees:', error)
      } finally {
        this.employeeLoading = false
      }
    },
    
    applyFilters() {
      const filters = {
        date_from: this.localFilters.dateRange[0],
        date_to: this.localFilters.dateRange[1],
        org_chart_structure_id: this.localFilters.org_chart_structure_id,
        employee_id: this.localFilters.employee_id
      }
      this.$emit('filter-change', filters)
    },
    
    async initializeFilters() {
      this.applyFilters()
    },
    
    setToday() {
      const today = moment().format('DD-MM-YYYY')
      this.localFilters.dateRange = [today, today]
      this.applyFilters()
    },
    
    setYesterday() {
      const yesterday = moment().subtract(1, 'days').format('DD-MM-YYYY')
      this.localFilters.dateRange = [yesterday, yesterday]
      this.applyFilters()
    },
    
    setThisWeek() {
      const startOfWeek = moment().startOf('week').format('DD-MM-YYYY')
      const endOfWeek = moment().endOf('week').format('DD-MM-YYYY')
      this.localFilters.dateRange = [startOfWeek, endOfWeek]
      this.applyFilters()
    },
    
    setThisMonth() {
      const startOfMonth = moment().startOf('month').format('DD-MM-YYYY')
      const endOfMonth = moment().endOf('month').format('DD-MM-YYYY')
      this.localFilters.dateRange = [startOfMonth, endOfMonth]
      this.applyFilters()
    }
  },
  watch: {
    orgChartStructures: {
      handler(val) {
        if (val && val.length > 0 && !this.localFilters.org_chart_structure_id) {
          this.initializeFilters()
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss">
.attendance-filters {
  position: sticky;
  top: 60px;
  z-index: 10;
  margin-bottom: 16px;
  
  .filters-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8eaec;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12);
    }
    
    @media (max-width: 768px) {
      padding: 16px;
      border-radius: 8px;
    }
  }
  
  .filter-group {
    display: flex;
    flex-direction: column;
    
    .filter-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 600;
      margin-bottom: 8px;
      font-size: 13px;
      color: #17233d;
      
      i {
        color: #667eea;
      }
      
      &.required::after {
        content: '*';
        color: #ed4014;
        margin-left: 4px;
      }
    }
  }
  
  .quick-filters {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 2px dashed #e8eaec;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    
    .quick-filters-label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #515a6e;
      
      i {
        color: #667eea;
      }
    }
    
    .quick-filter-buttons {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      
      button {
        border-radius: 20px;
        padding: 6px 16px;
        font-size: 12px;
        font-weight: 600;
        transition: all 0.2s ease;
        border: 2px solid #e8eaec;
        background: #ffffff;
        color: #515a6e;
        
        &:hover {
          border-color: #667eea;
          color: #667eea;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(102, 126, 234, 0.2);
        }
        
        &:active {
          transform: translateY(0);
        }
      }
    }
  }
  
  ::v-deep {
    .date-picker-custom {
      .ivu-input {
        border-radius: 8px;
        border: 2px solid #e8eaec;
        padding: 8px 12px;
        transition: all 0.2s ease;
        
        &:hover {
          border-color: #c5c8ce;
        }
        
        &:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
      }
    }
    
    .select-custom {
      .ivu-select-selection {
        border-radius: 8px;
        border: 2px solid #e8eaec;
        transition: all 0.2s ease;
        
        &:hover {
          border-color: #c5c8ce;
        }
      }
      
      &.ivu-select-focused {
        .ivu-select-selection {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
      }
    }
    
    .search-button {
      border-radius: 8px;
      font-weight: 600;
      height: 40px;
      border: none;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }
}

::v-deep .ivu-date-picker-transfer,
::v-deep .ivu-select-dropdown {
  z-index: 20 !important;
}

::v-deep .ivu-select-dropdown {
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8eaec;
  
  .ivu-select-item {
    padding: 8px 12px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f7f8fa;
      color: #667eea;
    }
    
    &.ivu-select-item-selected {
      color: white;
      font-weight: 600;
    }
  }
}

::v-deep .ivu-date-picker-transfer {
  .ivu-picker-panel-body {
    border-radius: 8px;
  }
  
  .ivu-date-picker-cells-cell-selected {
    
    div {
      color: white;
    }
  }
  
  .ivu-date-picker-cells-cell:hover {
    background: #f7f8fa;
    
    div {
      color: #667eea;
    }
  }
}
</style>
